// Configurações do ranking de games, na ordem em que aparecem no filtro.
// Lista fechada de propósito: o evento tem muitas outras cadastradas
// (LOGIN001, LIKE001...), que são de interação e vão para o outro card.
export const GAME_CONFIG_CODES = ["POSTPHOTOCREATE001", "MALA", "PASSARELA"];

// As duas funções abaixo recebem os códigos que a api devolveu e dizem quais
// entram em cada card. São passadas ao RankingList, que é o mesmo componente
// nos dois — só muda o recorte.

// Ranking Games: percorre a lista fixa, e não o retorno da api, para a ordem
// do filtro ser sempre a mesma
export function gameConfigs(codes = []) {
  return GAME_CONFIG_CODES.filter((code) => codes.includes(code));
}

// Ranking Interação: tudo que NÃO é game, na ordem em que a api devolveu.
// Aberta de propósito: configuração nova cadastrada no evento aparece aqui
// sozinha, sem precisar mexer no código.
export function interactionConfigs(codes = []) {
  return codes.filter((code) => !GAME_CONFIG_CODES.includes(code));
}

// Opção consolidada do filtro: soma a pontuação do participante em todas as
// configurações daquele card, e só nelas. Difere do antigo Ranking Geral, que
// somava o evento inteiro — aqui o Ranking Games não enxerga pontuação de
// interação e vice-versa.
//
// O código é um sentinela do front: nunca vai para a api, que recebe a lista de
// códigos da categoria. Não pode começar com MALA nem PASSARELA, senão
// isMatchConfig o trataria como configuração de partida (compara por prefixo).
export const TOTAL_RANKING_CODE = "TOTAL";

export const TOTAL_RANKING_OPTION = {
  code: TOTAL_RANKING_CODE,
  description: "Ranking total por categoria",
};

// Nome de arquivo a partir de um rótulo da tela ("Ranking Interação" →
// "ranking-interacao"). Sem acento de propósito: nome de download acentuado
// chega embaralhado em parte dos navegadores e sistemas de arquivos.
export function fileSlug(label) {
  return String(label)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

// Código da pontuação de acesso ao evento. É a fonte do gráfico de barras:
// cada registro de earned_points nesse código é um acesso pontuado, então a
// contagem por participante é o ranking de acessos.
export const ACCESS_CONFIG_CODE = "LOGIN001";

// Quantos participantes o gráfico mostra. O endpoint tem teto próprio (100);
// 30 é o pedido da tela e cabe no scroll horizontal sem virar uma parede.
export const ACCESS_TOP_LIMIT = 30;

// Rótulo legível de cada código de configuração, para o filtro não exibir
// coisas como POSTPHOTOCREATE001. Só exibição: o valor enviado à api continua
// sendo o código.
//
// Existe um mapa parecido em ranking/mockCodes.js (`optionCode`), usado pela
// tela de configurações; os textos de lá são em caixa alta e com outra
// redação, por isso este é separado. Código fora da lista aparece como veio —
// o evento pode cadastrar qualquer um, e esconder seria pior que mostrar.
const CONFIG_LABELS = {
  [TOTAL_RANKING_CODE]: TOTAL_RANKING_OPTION.description,
  LOGIN001: "Acessos",
  LIKE001: "Curtir um Post",
  LIKERECEIVED001: "Curtida Recebida",
  COMMENT001: "Comentar um Post",
  COMMENTRECEIVED001: "Comentário Recebido",
  POSTCREATE001: "Criar um Post",
  POSTPHOTOCREATE001: "Post com Foto",
};

export function labelForConfig(code) {
  if (!code) return "";

  return CONFIG_LABELS[String(code).toUpperCase()] ?? code;
}

// Colunas da lista, na ordem em que aparecem. As quatro primeiras e a de ações
// são as mesmas do Ranking Geral de /event/ranking — aqui sem o botão de
// excluir pontuação.
//
// `perMatch` marca o que só existe numa partida de um jogo: no Ranking Geral,
// que consolida o evento inteiro, essas colunas não vêm preenchidas e são
// escondidas por `columnsForConfig`.
export const RANKING_COLUMNS = [
  { key: "loginId", label: "ID do Participante" },
  { key: "name", label: "Participante" },
  { key: "totalPoints", label: "Pontuação" },
  // tempo e movimentos da partida numa coluna só ("00:05 / 6")
  { key: "match", label: "Tempo/Movimentos", perMatch: true },
  { key: "updatedAt", label: "Data/Hora da última pontuação" },
  { key: "actions", label: "Ações" },
];

// Configurações que registram uma partida e, por isso, têm movimentos e tempo.
//
// É uma allowlist, e não "tudo que não é o Ranking Geral": os códigos do
// filtro vêm das configurações de pontuação do evento, que incluem coisas como
// LOGIN001 e LIKE001 — nenhuma delas tem partida. A comparação é por prefixo
// porque o código cadastrado costuma vir numerado (MALA001).
export const MATCH_CONFIG_CODES = ["MALA", "PASSARELA"];

export function isMatchConfig(rankingCode) {
  if (!rankingCode) return false;

  const code = String(rankingCode).toUpperCase();
  return MATCH_CONFIG_CODES.some((prefix) => code.startsWith(prefix));
}

// Colunas visíveis para a configuração selecionada.
export function columnsForConfig(rankingCode) {
  if (isMatchConfig(rankingCode)) return RANKING_COLUMNS;

  return RANKING_COLUMNS.filter((column) => !column.perMatch);
}

// Tempo da partida em mm:ss. O valor vem em milissegundos, como o app dos
// jogos grava (elapsedSeconds * 1000). String vazia quando não há partida
// registrada, em vez de "00:00", que seria um tempo real de zero segundo.
function formatDuration(ms) {
  if (ms === null || ms === undefined || ms === "") return "";

  const totalSeconds = Math.floor(Number(ms) / 1000);
  if (!Number.isFinite(totalSeconds)) return "";

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

// Tempo e movimentos da partida numa string só ("00:05 / 6"), usada na tabela
// e na planilha. Vazia quando não há partida registrada — "00:00 / 0" seria
// uma partida real de zero segundo e nenhum movimento. A ordenação continua
// sendo feita pelo endpoint, por tempo e depois movimentos, campos separados.
export function formatMatch(row) {
  const time = formatDuration(row?.duration);
  const moves = row?.attempts;
  const semMovimentos = moves === null || moves === undefined || moves === "";

  if (!time && semMovimentos) return "";

  return `${time || "--"} / ${semMovimentos ? "--" : moves}`;
}
